import { dashboardApi } from '@/store/api/base/api.base';
import { walkPages } from '@/store/api/base/api.walk';
import type { TypeCustomer, TypeCustomerStatus, TypeViesCompany } from '@/types/customer/types.customer';
import type { TypeAppDispatch } from '@/store/store';
import { dropById, upsertById } from '@/store/api/base/api.cache';

const PAGE_SIZE = 200;
const MAX_PAGES = 25;

const byName = (a: TypeCustomer, b: TypeCustomer) => a.name.localeCompare(b.name, 'it');

export const customerApi = dashboardApi.injectEndpoints({
    endpoints: (build) => ({
        listCustomers: build.query<TypeCustomer[], void>({
            queryFn: async () => {
                const walked = await walkPages<TypeCustomer>({
                    url: '/customers',
                    fallback: 'Non è stato possibile caricare i clienti',
                    params: { sort: 'name' },
                    pageSize: PAGE_SIZE,
                    maxPages: MAX_PAGES,
                });
                if ('error' in walked) return walked;
                const items = walked.data;

                return { data: items };
            },
            providesTags: ['Customer'],
        }),

        lookupVies: build.query<TypeViesCompany, string>({
            query: (vatNumber) => ({ url: '/customers/vies', params: { vatNumber } }),
            keepUnusedDataFor: 600,
        }),

        readCustomer: build.query<TypeCustomer, string>({
            query: (id) => ({ url: `/customers/${id}` }),
            providesTags: (_result, _error, id) => [{ type: 'Customer', id }],
        }),

        createCustomer: build.mutation<TypeCustomer, TypeCustomer>({
            query: (customer) => ({ url: '/customers', method: 'post', data: { customer: toInput(customer) } }),
            onQueryStarted: async (_customer, { dispatch, queryFulfilled }) => {
            try {
                    const { data } = await queryFulfilled;
                    dispatch(customerApi.util.updateQueryData('listCustomers', undefined, (draft) => {
                        if (draft.some((entry) => entry.id === data.id)) return;
                        draft.push(data);
                        draft.sort(byName);
                    }));
                    writeDetail(dispatch, data);
                } catch {
                }
            },
        }),

        updateCustomer: build.mutation<TypeCustomer, { id: string; customer: TypeCustomer }>({
            query: ({ id, customer }) => ({ url: `/customers/${id}`, method: 'patch', data: { customer: toInput(customer) } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(customerApi.util.updateQueryData('listCustomers', undefined, (draft) => {
                        upsertById(draft, data);
                        draft.sort(byName);
                    }));
                    writeDetail(dispatch, data);
                } catch {
                }
            },
        }),

        changeCustomerStatus: build.mutation<TypeCustomer, { id: string; status: TypeCustomerStatus }>({
            query: ({ id, status }) => ({ url: `/customers/${id}/status`, method: 'patch', data: { status } }),
            onQueryStarted: async ({ id, status }, { dispatch, queryFulfilled }) => {
                const patches = [
                    dispatch(customerApi.util.updateQueryData('listCustomers', undefined, (draft) => {
                        const entry = draft.find((item) => item.id === id);
                        if (entry) entry.status = status;
                    })),
                    dispatch(customerApi.util.updateQueryData('readCustomer', id, (draft) => {
                        draft.status = status;
                    })),
                ];

                try {
                    const { data } = await queryFulfilled;
                    dispatch(customerApi.util.updateQueryData('listCustomers', undefined, (draft) => {
                        const index = draft.findIndex((item) => item.id === data.id);
                        if (index >= 0) draft[index] = data;
                    }));
                    writeDetail(dispatch, data);
                } catch {
                    patches.forEach((patch) => patch.undo());
                }
            },
        }),

        removeCustomer: build.mutation<null, string>({
            query: (id) => ({ url: `/customers/${id}`, method: 'delete' }),
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                    dispatch(customerApi.util.updateQueryData('listCustomers', undefined, (draft) => {
                        dropById(draft, id);
                    }));
                } catch {
                }
            },
        }),
    }),
});

const writeDetail = (dispatch: TypeAppDispatch, customer: TypeCustomer) => {
    dispatch(customerApi.util.updateQueryData('readCustomer', customer.id, () => customer));
};

const toInput = (customer: TypeCustomer) => {
    const { id, createdAt, updatedAt, address, referent, email, phone, ...input } = customer;
    return input;
};

export const {
    useListCustomersQuery,
    useReadCustomerQuery,
    useLookupViesQuery,
    useCreateCustomerMutation,
    useUpdateCustomerMutation,
    useChangeCustomerStatusMutation,
    useRemoveCustomerMutation,
} = customerApi;

export default customerApi;
